export const POLL_SELECTION_MODES = ['single', 'multiple'] as const
export const POLL_RESULT_VISIBILITIES = ['hidden', 'after_vote', 'after_reveal'] as const
export const POLL_STATUSES = ['draft', 'open', 'closed', 'revealed'] as const

export type PollSelectionMode = (typeof POLL_SELECTION_MODES)[number]
export type PollResultVisibility = (typeof POLL_RESULT_VISIBILITIES)[number]
export type PollStatus = (typeof POLL_STATUSES)[number]

export type PollInput = {
  tripId: string
  title: string
  description: string
  imageUrl: string | null
  place: string | null
  price: string | null
  selectionMode: PollSelectionMode
  resultVisibility: PollResultVisibility
  closesAt: string | null
  options: string[]
}

export type PollClock = Pick<PollInput, 'closesAt'> & { status: PollStatus }
export type VoteSelection = { optionId: string; selected: boolean }

function optionalHttpUrl(value: string | null): string | null {
  const clean = value?.trim() ?? null
  if (!clean) return null
  try {
    const parsed = new URL(clean)
    if (!['https:', 'http:'].includes(parsed.protocol)) throw new Error()
    return clean
  } catch {
    throw new Error('L’image du vote doit utiliser un lien web valide.')
  }
}

export function validatePollInput(input: PollInput): PollInput {
  const title = input.title.trim()
  const description = input.description.trim()
  const place = input.place?.trim() ?? null
  const price = input.price?.trim() ?? null
  const options = input.options.map((option) => option.trim()).filter(Boolean)
  if (title.length < 1 || title.length > 100) throw new Error('Le titre du vote est invalide.')
  if (description.length > 500) throw new Error('La description du vote est trop longue.')
  if ((place?.length ?? 0) > 120) throw new Error('Le lieu du vote est trop long.')
  if ((price?.length ?? 0) > 40) throw new Error('Le prix indicatif est trop long.')
  if (!POLL_SELECTION_MODES.includes(input.selectionMode)) throw new Error('Type de choix invalide.')
  if (!POLL_RESULT_VISIBILITIES.includes(input.resultVisibility)) throw new Error('Visibilité des résultats invalide.')
  if (options.length < 2) throw new Error('Ajoutez au moins deux choix.')
  if (options.length > 12) throw new Error('Un vote accepte au plus douze choix.')
  if (new Set(options.map((option) => option.toLocaleLowerCase('fr'))).size !== options.length) {
    throw new Error('Chaque choix doit être unique.')
  }
  if (input.closesAt !== null && !Number.isFinite(Date.parse(input.closesAt))) {
    throw new Error('L’échéance du vote est invalide.')
  }
  return {
    ...input,
    title,
    description,
    imageUrl: optionalHttpUrl(input.imageUrl),
    place,
    price,
    options,
  }
}

export function pollStatusAt(poll: PollClock, now = new Date()): PollStatus {
  if (poll.status !== 'open' || poll.closesAt === null) return poll.status
  return now.getTime() >= Date.parse(poll.closesAt) ? 'closed' : 'open'
}

export function canVoteInPoll(poll: PollClock, now = new Date()): boolean {
  return pollStatusAt(poll, now) === 'open'
}

export function canSeePollResults(
  poll: Pick<PollClock, 'status'> & { resultVisibility: PollResultVisibility },
  viewer: { hasVoted: boolean; isAdmin: boolean },
): boolean {
  if (viewer.isAdmin) return true
  if (poll.resultVisibility === 'hidden') return false
  if (poll.resultVisibility === 'after_vote') return viewer.hasVoted
  return poll.status === 'revealed'
}

export function applyVoteSelection(
  current: VoteSelection[],
  optionId: string,
  selected: boolean,
  mode: PollSelectionMode,
): VoteSelection[] {
  const found = current.some((selection) => selection.optionId === optionId)
  const next = current.map((selection) => {
    if (selection.optionId === optionId) {
      return { ...selection, selected }
    }
    if (mode === 'single' && selected) return { ...selection, selected: false }
    return selection
  })
  if (!found) next.push({ optionId, selected })
  return next
}
